import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { DashboardTemplate } from 'modules/v2/templates';
import { Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import {
  createModOrder,
  getModOrder,
  payModOrder,
  submitModOrderAudience,
} from 'modules/api/digitalServices/mailOnDemand';
import {
  getRouteMailOnDemandOrder,
  getRouteMailOnDemandResume,
} from 'modules/v2/routes/navigation';

import { PRINT_CREDITS_BALANCE_QUERY_KEY } from 'modules/v2/common/hooks/usePrintCreditsBalance';

import WizardStepsHeader from '../components/WizardStepsHeader';
import strings from '../strings';
import {
  AUDIENCE_MODE,
  MAX_PAYING_POLLS,
  ORDER_STATUS,
  RESOLUTION_POLL_INTERVAL_MS,
  WIZARD_STEP,
} from '../constants';
import {
  buildAudienceCriteria,
  getWizardStepForOrder,
  isSameAsset,
  isSameAudienceCriteria,
} from '../utils';
import AssetStep from './steps/AssetStep';
import AudienceStep from './steps/AudienceStep';
import ResolutionWaitStep from './steps/ResolutionWaitStep';
import ReviewStep from './steps/ReviewStep';
import PayStep from './steps/PayStep';
import ConfirmationStep from './steps/ConfirmationStep';

export const MailOnDemandWizard = ({ orderId }) => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(orderId ? null : WIZARD_STEP.ASSET);
  const [order, setOrder] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [mode, setMode] = useState(AUDIENCE_MODE.ALL);
  const [tagsInput, setTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [resolutionFailed, setResolutionFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);
  const [payingTimedOut, setPayingTimedOut] = useState(false);
  const [resumeError, setResumeError] = useState(false);

  // Resume an existing order at the correct step.
  // The same mounted instance serves /new and /orders/:id/continue (the
  // wizard history.replace()s between them mid-flow — see the route config),
  // so this effect must tolerate the URL changing under it.
  useEffect(() => {
    if (!orderId) {
      // Back on /new with an order in memory (e.g. the sidebar link after
      // finishing an order): reset to a clean first step — previously the
      // route remount did this implicitly.
      if (order) {
        setOrder(null);
        setSelectedAsset(null);
        setMode(AUDIENCE_MODE.ALL);
        setTagsInput('');
        setSubmitError(false);
        setResolutionFailed(false);
        setPayError(null);
        setResumeError(false);
        setStep(WIZARD_STEP.ASSET);
      }
      return undefined;
    }

    if (order?._id === orderId) {
      // The URL just caught up with in-memory state (history.replace after
      // create): a refetch here would race the in-flight submit and bounce
      // the member back a step.
      return undefined;
    }

    // A DIFFERENT order's id arrived (navigating between two resume URLs):
    // drop the previous order's derived state and show the loader first, or
    // order A's step/asset flashes under order B's URL until the refetch lands.
    if (order && order._id !== orderId) {
      setOrder(null);
      setSelectedAsset(null);
      setResolutionFailed(false);
      setPayError(null);
      setPayingTimedOut(false);
      setStep(null);
    }

    let cancelled = false;

    getModOrder(orderId)
      .then((existing) => {
        if (cancelled) return;

        const resumeStep = getWizardStepForOrder(existing);

        if (!resumeStep) {
          // Order is no longer editable in the wizard; send to detail page
          history.replace(getRouteMailOnDemandOrder(existing._id));
          return;
        }

        setOrder(existing);
        setSelectedAsset(existing.asset || null);

        if (existing.audienceCriteria) {
          setMode(existing.audienceCriteria.mode || AUDIENCE_MODE.ALL);
          setTagsInput((existing.audienceCriteria.tags || []).join(', '));
        }

        setResolutionFailed(existing.status === ORDER_STATUS.RESOLUTION_FAILED);
        setStep(resumeStep);
      })
      .catch(() => {
        if (!cancelled) setResumeError(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Poll the order while the audience is being resolved
  useEffect(() => {
    if (step !== WIZARD_STEP.RESOLUTION || resolutionFailed || !order?._id) return undefined;

    const interval = setInterval(async () => {
      try {
        const latest = await getModOrder(order._id);

        setOrder(latest);

        if (latest.status === ORDER_STATUS.REVIEW) {
          setStep(WIZARD_STEP.REVIEW);
        } else if (latest.status === ORDER_STATUS.RESOLUTION_FAILED) {
          setResolutionFailed(true);
        }
      } catch (error) {
        // Transient polling errors are ignored; the next tick retries
      }
    }, RESOLUTION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, resolutionFailed, order?._id]);

  // Poll an interrupted pay (resumed at PAYING) until the debit settles to
  // PAID (→ confirmation) or the lock releases back to REVIEW. The normal pay
  // flow never sits here — payModOrder returns the already-PAID order. The
  // poll is bounded (MAX_PAYING_POLLS): a debit that never settles flips
  // payingTimedOut so the Pay step can offer a recovery action instead of
  // stranding the member on a dead screen.
  useEffect(() => {
    if (
      order?.status !== ORDER_STATUS.PAYING ||
      !order?._id ||
      payingTimedOut
    ) {
      return undefined;
    }

    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const latest = await getModOrder(order._id);

        setOrder(latest);

        if (latest.status === ORDER_STATUS.PAID) {
          queryClient.invalidateQueries(PRINT_CREDITS_BALANCE_QUERY_KEY);
          setStep(WIZARD_STEP.CONFIRMATION);
          return;
        }
        if (latest.status === ORDER_STATUS.REVIEW) {
          // Only pull the member back to Review from the Pay step — never yank
          // them off a confirmation they may already be reading.
          setStep((current) =>
            current === WIZARD_STEP.PAY ? WIZARD_STEP.REVIEW : current,
          );
          return;
        }
      } catch (error) {
        // Transient polling errors are ignored; the next tick retries
      }

      if (attempts >= MAX_PAYING_POLLS) {
        setPayingTimedOut(true);
      }
    }, RESOLUTION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status, order?._id, payingTimedOut]);

  const handleAudienceContinue = async () => {
    setSubmitting(true);
    setSubmitError(false);

    try {
      const audienceCriteria = buildAudienceCriteria(mode, tagsInput);
      let current = order;

      const canReuseDraft =
        current &&
        current.status === ORDER_STATUS.DRAFT &&
        isSameAsset(current.asset, selectedAsset) &&
        isSameAudienceCriteria(current.audienceCriteria, audienceCriteria);

      if (!canReuseDraft) {
        // The backend resolves title/pdfUrl server-side from the member's
        // own asset library; only the reference is sent.
        const { kind, sourceId } = selectedAsset;

        current = await createModOrder({
          asset: { kind, sourceId },
          audienceCriteria,
        });

        // Keep the created draft even if the submit below fails, so a retry
        // reuses it instead of creating an orphan order per click — and sync
        // the URL so refresh/recovery resumes this order.
        setOrder(current);
        history.replace(getRouteMailOnDemandResume(current._id));
      }

      const submitted = await submitModOrderAudience(current._id);

      setOrder(submitted);
      setResolutionFailed(false);
      setStep(WIZARD_STEP.RESOLUTION);
    } catch (error) {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolutionRetry = async () => {
    setRetrying(true);

    try {
      const submitted = await submitModOrderAudience(order._id);

      setOrder(submitted);
      setResolutionFailed(false);
    } catch (error) {
      setResolutionFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  const handlePay = async () => {
    setPaying(true);
    // A retry from the timed-out recovery state re-arms the poll.
    setPayingTimedOut(false);

    try {
      const paid = await payModOrder(order._id);

      setOrder(paid);
      setPayError(null);
      // The pay debited the ledger — refresh every credits display (header,
      // MOD widget) without requiring a reload.
      queryClient.invalidateQueries(PRINT_CREDITS_BALANCE_QUERY_KEY);

      // Only declare success when the debit actually settled. If the response
      // is still PAYING (async debit), stay on Pay and let the bounded poll
      // drive it — never show a confirmation for an unsettled order.
      if (paid.status === ORDER_STATUS.PAID) {
        setStep(WIZARD_STEP.CONFIRMATION);
      }
    } catch (error) {
      if (error?.response?.status === 402) {
        const { required, balance, shortfall } = error.response.data || {};

        setPayError({ type: 'insufficient', required, balance, shortfall });
      } else {
        setPayError({ type: 'generic' });
      }
    } finally {
      setPaying(false);
    }
  };

  if (resumeError) {
    return <p className="py-12 text-center text-neutral-500">{strings.common.genericError}</p>;
  }

  if (step === null) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.wizard.loadingOrder}</span>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case WIZARD_STEP.ASSET:
        return (
          <AssetStep
            selectedAsset={selectedAsset}
            onSelectAsset={setSelectedAsset}
            onNext={() => setStep(WIZARD_STEP.AUDIENCE)}
          />
        );
      case WIZARD_STEP.AUDIENCE:
        return (
          <AudienceStep
            mode={mode}
            tagsInput={tagsInput}
            onModeChange={setMode}
            onTagsChange={setTagsInput}
            onBack={() => setStep(WIZARD_STEP.ASSET)}
            onContinue={handleAudienceContinue}
            submitting={submitting}
          />
        );
      case WIZARD_STEP.RESOLUTION:
        return (
          <ResolutionWaitStep
            failed={resolutionFailed}
            onRetry={handleResolutionRetry}
            retrying={retrying}
          />
        );
      case WIZARD_STEP.REVIEW:
        return (
          <ReviewStep
            order={order}
            onOrderChange={setOrder}
            onContinue={() => setStep(WIZARD_STEP.PAY)}
          />
        );
      case WIZARD_STEP.PAY:
        return (
          <PayStep
            order={order}
            onPay={handlePay}
            onBack={() => setStep(WIZARD_STEP.REVIEW)}
            paying={paying}
            // A resumed PAYING order is mid-debit: show the processing note and
            // lock the buttons while the bounded poll settles. If it never
            // settles the wizard flips payingTimedOut and PayStep swaps to a
            // recovery action (retry / view order) so the member is never
            // stranded.
            processing={order?.status === ORDER_STATUS.PAYING && !payingTimedOut}
            payingTimedOut={payingTimedOut}
            payError={payError}
          />
        );
      case WIZARD_STEP.CONFIRMATION:
        return <ConfirmationStep order={order} />;
      default:
        return null;
    }
  };

  // Backward navigation via the stepper is only safe before the audience is
  // submitted — once an order exists the asset/audience are locked to it.
  const canNavigateSteps =
    step === WIZARD_STEP.ASSET || step === WIZARD_STEP.AUDIENCE;
  const handleStepSelect = (targetStep) => {
    if (!canNavigateSteps) return;
    if (targetStep === WIZARD_STEP.ASSET) {
      setStep(WIZARD_STEP.ASSET);
    } else if (targetStep === WIZARD_STEP.AUDIENCE && selectedAsset) {
      setStep(WIZARD_STEP.AUDIENCE);
    }
  };

  return (
    <div>
      <WizardStepsHeader
        currentStep={step}
        onStepSelect={canNavigateSteps ? handleStepSelect : undefined}
      />
      {submitError && (
        <p className="mb-4 text-sm text-error-500">{strings.common.genericError}</p>
      )}
      {renderStep()}
    </div>
  );
};

const MailOnDemandWizardPage = () => {
  const { orderId } = useParams();

  return (
    <DashboardTemplate hasSidebar title={strings.wizard.pageTitle}>
      <MailOnDemandWizard orderId={orderId} />
    </DashboardTemplate>
  );
};

export default MailOnDemandWizardPage;
